import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import ModalShell from "./ModalShell";
import FileDropzone from "./FileDropzone";
import { Collection } from "../../../api/derin";
import { captionLabelSx, collectionColors, inputSx, subtitleSx } from "./tokens";

interface AddVariantModalProps {
  open: boolean;
  collection: Collection | null;
  onClose: () => void;
  onSubmit: (payload: { label: string; file: File }) => void;
  isSubmitting?: boolean;
}

const AddVariantModal: React.FC<AddVariantModalProps> = ({
  open,
  collection,
  onClose,
  onSubmit,
  isSubmitting = false,
}) => {
  const [label, setLabel] = useState("");
  const [file, setFile] = useState<File | null>(null);

  const close = () => {
    setLabel("");
    setFile(null);
    onClose();
  };

  const duplicateLabel = Boolean(
    collection?.variants.some((variant) => variant.label.trim().toLowerCase() === label.trim().toLowerCase())
  );
  const canSubmit = Boolean(label.trim()) && Boolean(file) && !duplicateLabel;

  return (
    <ModalShell
      open={open}
      onClose={close}
      title="Add Variant"
      subtitle={
        collection
          ? `Add a survey variant to "${collection.name}". It keeps its own version history.`
          : undefined
      }
      confirmLabel="Add Variant"
      onConfirm={() => {
        if (canSubmit && file) {
          onSubmit({ label: label.trim(), file });
        }
      }}
      confirmDisabled={!canSubmit}
      isSubmitting={isSubmitting}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
        <Typography sx={captionLabelSx}>Label</Typography>
        <Box
          component="input"
          type="text"
          placeholder="e.g. Spanish, Panel B"
          value={label}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => setLabel(event.target.value)}
          sx={inputSx}
        />
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
        <Typography sx={captionLabelSx}>YAML file</Typography>
        <FileDropzone file={file} onSelect={setFile} placeholder="Click to upload or drag and drop" />
      </Box>

      {duplicateLabel && (
        <Typography sx={{ ...subtitleSx, color: collectionColors.danger }}>
          This collection already has a variant with that label.
        </Typography>
      )}
    </ModalShell>
  );
};

export default AddVariantModal;
